import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaTimes, FaFileAlt, FaBlog, FaBriefcase, FaHome, FaEnvelope } from 'react-icons/fa';
import { blogAPI, servicesAPI } from '../utils/api';
import { useSearch } from '../context/SearchContext';

const SearchOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
`;

const SearchModal = styled(motion.div)`
  background: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xl);
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: var(--space-lg);
  border-bottom: 1px solid var(--secondary-200);
  gap: var(--space-md);
`;

const SearchIcon = styled(FaSearch)`
  color: var(--secondary-400);
  font-size: 1.25rem;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 1rem;
  color: var(--secondary-700);
  background: transparent;

  &::placeholder {
    color: var(--secondary-400);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--secondary-400);
  cursor: pointer;
  padding: var(--space-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);

  &:hover {
    background: var(--secondary-100);
    color: var(--secondary-700);
  }

  &:focus {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ResultsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: calc(80vh - 80px);
`;

const ResultGroup = styled.div`
  padding: var(--space-md) 0;
`;

const GroupTitle = styled.div`
  padding: 0 var(--space-lg) var(--space-sm);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--secondary-500);
`;

const ResultItem = styled(motion.button)`
  width: 100%;
  padding: var(--space-md) var(--space-lg);
  border: none;
  background: ${props => props.isActive ? 'var(--primary-50)' : 'transparent'};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: var(--space-md);
  transition: background var(--transition-fast);
  border-left: 3px solid ${props => props.isActive ? 'var(--primary-600)' : 'transparent'};

  &:hover {
    background: var(--secondary-50);
  }

  &:focus {
    outline: 2px solid var(--primary-600);
    outline-offset: -2px;
  }
`;

const ResultIcon = styled.div`
  color: var(--primary-600);
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 2px;
`;

const ResultContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ResultTitle = styled.div`
  font-weight: 600;
  color: var(--secondary-700);
  margin-bottom: var(--space-xs);
  font-size: 0.95rem;
`;

const ResultDescription = styled.div`
  font-size: 0.85rem;
  color: var(--secondary-500);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ResultMeta = styled.div`
  font-size: 0.75rem;
  color: var(--secondary-400);
  margin-top: var(--space-xs);
`;

const EmptyState = styled.div`
  padding: var(--space-xl);
  text-align: center;
  color: var(--secondary-500);
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  color: var(--secondary-300);
  margin-bottom: var(--space-md);
`;

const EmptyStateText = styled.div`
  font-size: 0.95rem;
  margin-bottom: var(--space-xs);
`;

const EmptyStateHint = styled.div`
  font-size: 0.85rem;
  color: var(--secondary-400);
`;

const KeyboardHint = styled.div`
  padding: var(--space-md) var(--space-lg);
  border-top: 1px solid var(--secondary-200);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--secondary-500);
  background: var(--secondary-50);
`;

const KeyboardShortcut = styled.span`
  display: inline-flex;
  gap: 2px;
  align-items: center;
`;

const Kbd = styled.kbd`
  background: white;
  border: 1px solid var(--secondary-300);
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 0.7rem;
  font-family: monospace;
  box-shadow: 0 1px 0 var(--secondary-300);
`;

const GlobalSearch = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isOpen, closeSearch } = useSearch();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Search function
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    const queryLower = searchQuery.toLowerCase();
    const searchResults = [];

    // Static pages that can be searched
    const staticPages = [
      {
        id: 'home',
        type: 'page',
        title: t('common.home'),
        description: t('home.subtitle'),
        path: '/',
        icon: FaHome
      },
      {
        id: 'services',
        type: 'page',
        title: t('common.services'),
        description: t('services.subtitle'),
        path: '/services',
        icon: FaBriefcase
      },
      {
        id: 'blog',
        type: 'page',
        title: t('common.blog'),
        description: t('blog.subtitle'),
        path: '/blog',
        icon: FaBlog
      },
      {
        id: 'contact',
        type: 'page',
        title: t('common.contact'),
        description: t('contact.subtitle'),
        path: '/contact',
        icon: FaEnvelope
      }
    ];

    try {
      // Search static pages
      staticPages.forEach(page => {
        const titleMatch = page.title.toLowerCase().includes(queryLower);
        const descMatch = page.description?.toLowerCase().includes(queryLower);
        if (titleMatch || descMatch) {
          searchResults.push({
            ...page,
            relevance: titleMatch ? 2 : 1
          });
        }
      });

      // Search blog posts
      try {
        const blogResponse = await blogAPI.getAll({ limit: 20 }, i18n.language);
        const blogPosts = blogResponse.data.posts || [];
        blogPosts.forEach(post => {
          const titleMatch = post.title?.toLowerCase().includes(queryLower);
          const excerptMatch = post.excerpt?.toLowerCase().includes(queryLower);
          const categoryMatch = post.category?.toLowerCase().includes(queryLower);
          const tagsMatch = post.tags?.some(tag => tag.toLowerCase().includes(queryLower));
          
          if (titleMatch || excerptMatch || categoryMatch || tagsMatch) {
            searchResults.push({
              id: post._id,
              type: 'blog',
              title: post.title,
              description: post.excerpt,
              path: `/blog/${post.slug}`,
              icon: FaFileAlt,
              meta: `${post.category} • ${post.readTime} ${t('blog.minRead')}`,
              relevance: (titleMatch ? 3 : 0) + (excerptMatch ? 1 : 0) + (categoryMatch ? 1 : 0) + (tagsMatch ? 1 : 0)
            });
          }
        });
      } catch (error) {
        console.error('Error searching blog posts:', error);
      }

      // Search services
      try {
        const servicesResponse = await servicesAPI.getAll();
        const services = servicesResponse.data || [];
        services.forEach(service => {
          const titleMatch = service.title?.toLowerCase().includes(queryLower);
          const descMatch = service.description?.toLowerCase().includes(queryLower);
          const shortDescMatch = service.shortDescription?.toLowerCase().includes(queryLower);
          const featuresMatch = service.features?.some(f => f.toLowerCase().includes(queryLower));
          
          if (titleMatch || descMatch || shortDescMatch || featuresMatch) {
            searchResults.push({
              id: service._id,
              type: 'service',
              title: service.title,
              description: service.shortDescription || service.description,
              path: '/services',
              icon: FaBriefcase,
              meta: service.price,
              relevance: (titleMatch ? 3 : 0) + (descMatch ? 1 : 0) + (shortDescMatch ? 1 : 0) + (featuresMatch ? 1 : 0)
            });
          }
        });
      } catch (error) {
        console.error('Error searching services:', error);
      }

      // Sort by relevance
      searchResults.sort((a, b) => (b.relevance || 0) - (a.relevance || 0));
      setResults(searchResults.slice(0, 10)); // Limit to 10 results
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [i18n.language, t]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, performSearch]);


  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle result selection
  const handleResultClick = useCallback((result) => {
    navigate(result.path);
    closeSearch();
    setQuery('');
    setResults([]);
  }, [navigate, closeSearch]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        handleResultClick(results[selectedIndex]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, handleResultClick]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current && results.length > 0) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, results]);

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    const group = result.type === 'blog' ? 'blog' : 
                  result.type === 'service' ? 'services' : 'pages';
    if (!acc[group]) acc[group] = [];
    acc[group].push(result);
    return acc;
  }, {});

  const groupLabels = {
    pages: t('search.pages'),
    blog: t('search.blogPosts'),
    services: t('search.services')
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <SearchOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeSearch();
              setQuery('');
              setResults([]);
            }
          }}
        >
          <SearchModal
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <SearchInputContainer>
              <SearchIcon />
              <SearchInput
                ref={inputRef}
                type="text"
                placeholder={t('search.placeholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={t('search.ariaLabel')}
              />
              <CloseButton
                onClick={() => {
                  closeSearch();
                  setQuery('');
                  setResults([]);
                }}
                aria-label={t('search.close')}
              >
                <FaTimes />
              </CloseButton>
            </SearchInputContainer>

            <ResultsContainer ref={resultsRef}>
              {isLoading ? (
                <EmptyState>
                  <EmptyStateText>{t('search.loading')}</EmptyStateText>
                </EmptyState>
              ) : query.trim() && results.length === 0 ? (
                <EmptyState>
                  <EmptyStateIcon>🔍</EmptyStateIcon>
                  <EmptyStateText>{t('search.noResults')}</EmptyStateText>
                  <EmptyStateHint>{t('search.noResultsHint')}</EmptyStateHint>
                </EmptyState>
              ) : query.trim() && results.length > 0 ? (
                Object.entries(groupedResults).map(([group, items]) => (
                  <ResultGroup key={group}>
                    <GroupTitle>{groupLabels[group] || group}</GroupTitle>
                    {items.map((result, index) => {
                      const globalIndex = results.indexOf(result);
                      const Icon = result.icon;
                      return (
                        <ResultItem
                          key={result.id || result.path}
                          isActive={selectedIndex === globalIndex}
                          onClick={() => handleResultClick(result)}
                          whileHover={{ x: 2 }}
                          transition={{ duration: 0.1 }}
                        >
                          <ResultIcon>
                            <Icon />
                          </ResultIcon>
                          <ResultContent>
                            <ResultTitle>{result.title}</ResultTitle>
                            {result.description && (
                              <ResultDescription>{result.description}</ResultDescription>
                            )}
                            {result.meta && <ResultMeta>{result.meta}</ResultMeta>}
                          </ResultContent>
                        </ResultItem>
                      );
                    })}
                  </ResultGroup>
                ))
              ) : (
                <EmptyState>
                  <EmptyStateIcon>⌘</EmptyStateIcon>
                  <EmptyStateText>{t('search.startTyping')}</EmptyStateText>
                  <EmptyStateHint>{t('search.startTypingHint')}</EmptyStateHint>
                </EmptyState>
              )}
            </ResultsContainer>

            <KeyboardHint>
              <span>{t('search.navigationHint')}</span>
              <KeyboardShortcut>
                <Kbd>{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}</Kbd>
                <Kbd>K</Kbd>
              </KeyboardShortcut>
            </KeyboardHint>
          </SearchModal>
        </SearchOverlay>
      )}
    </AnimatePresence>
  );
};

export default GlobalSearch;

